import { forwardRef } from 'react';

import { styled } from '@mui/material';

import { useMainLayout, SidebarState } from '@cast/shared-layout-ui';

import { Box } from '@cast/design-system';
import type { BoxProps } from '@cast/design-system';

const NO_SIDEBAR_WIDTH = {
  default: 'calc(100vw - 96px)',
  xl: 1560,
  lg: 1560,
  md: 1512,
  sm: 1320,
};

const MINIMIZED_SIDEBAR_WIDTH = {
  default: 'calc(100vw - 144px)',
  xl: 1680,
  lg: 1680,
  md: 1536,
  sm: 1296,
};

const MAXIMIZED_SIDEBAR_WIDTH = {
  default: 'calc(100vw - 312px)',
  xl: 1512,
  lg: 1512,
  md: 1368,
  sm: 1128,
};

const ContentContainerRoot = styled(Box, {
  target: 'Layout-ContentContainer',
  shouldForwardProp: (propName: PropertyKey) => propName !== 'ownerState',
})<
  BoxProps & {
    ownerState: {
      sidebarState: SidebarState;
      isFullWidthContent?: boolean;
    };
  }
>(({ theme: { breakpoints }, ownerState }) => {
  let contentWidthMap = NO_SIDEBAR_WIDTH;

  if (ownerState.isFullWidthContent) {
    return {
      gridColumn: 'main',
      gridRow: 'content',
      [breakpoints.up('lg')]: {
        gridColumn: 'main / -1',
      },
    };
  }

  if (ownerState.sidebarState === 'minimized') {
    contentWidthMap = MINIMIZED_SIDEBAR_WIDTH;
  }

  if (ownerState.sidebarState === 'maximized') {
    contentWidthMap = MAXIMIZED_SIDEBAR_WIDTH;
  }

  return {
    position: 'relative',
    margin: '0 auto',
    width: contentWidthMap.default,

    [breakpoints.up('xl')]: {
      width: contentWidthMap.xl,
      marginLeft: ownerState.sidebarState ? '40px' : undefined,
    },
    [breakpoints.between('lg', 'xl')]: {
      width: contentWidthMap.lg,
      marginLeft: ownerState.sidebarState ? '40px' : undefined,
    },
    [breakpoints.between('md', 'lg')]: {
      width: contentWidthMap.md,
    },
    [breakpoints.between('sm', 'md')]: {
      width: contentWidthMap.sm,
    },
  };
});

type Props = BoxProps & { isFullWidthContent?: boolean };

export const ContentContainer = forwardRef<HTMLDivElement, Props>(
  ({ isFullWidthContent, ...props }, ref) => {
    const { sidebarState, isInsideMainLayout } = useMainLayout();

    if (!isInsideMainLayout) {
      // It means the OP layout is used.
      // To not replace all usages now, when it's not clear whether OP layout will be approved for everyone, simply add proper OP layout here.
      // TODO: in case OP layout is approved - just get rid of the non-OP stuff here, basically everything :), and move it to the OP layout folder.
      return <Box width="100%" px={24} {...props} />;
    }

    return (
      <ContentContainerRoot
        {...props}
        ownerState={{ sidebarState, isFullWidthContent }}
        ref={ref}
      />
    );
  }
);

ContentContainer.displayName = 'ContentContainer';
